import { LOCAL_STORAGE } from './constants';
import type { SortParameters } from '@mimir/common/dto/searchModels';
import { isInstanceOfSortParameters, SortOrder, SortOptions } from '@mimir/common/dto/searchModels';
import type { TimeFormat } from '@mimir/client-common/scheduler/scheduler';
import type { ViewSize } from '@mimir/client-common/util/premiere';
import { isPremiere } from '@mimir/web/nle/adapters/cep/cepEnvironment';
import type { DialogChoices } from '@mimir/web/model/dialogChoices';
import { defaultDialogChoices } from '@mimir/web/model/dialogChoices';
import type { SupportedLiveSourceProtocol } from '@mimir/common/dto/liveRecordingParameters';
import { supportedLiveSourceProtocols } from '@mimir/common/dto/liveRecordingParameters';
import { parseOrNull } from '@mimir/common/util/general';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { ConsoleLogger } from '@mimir/common/util/logging';
import type { ViewType } from '@mimir/common/dto/mdfModels';
import type { GlobalApiResponse } from '@mimir/common/global/globalApiClient';
import type { GlobalUserResponseDto } from '@mimir/common/dto/globalUserDtos';
import isObject from 'lodash/isObject';
import type { VisualizationMode } from '@mimir/browser/cutter/audioVisualizer';
import type { PlaybackQuality } from '@mimir/common/types/playbackQuality';
import type { ResolvedTheme } from '@mimir/common/dto/theme';

export interface UserInterfaceSettings {
  isMuted: boolean;
  playerVolume: number; // Remembers player volume across the app
  searchThumbnailSize: number; // Grid size in search grid view
  searchMenuExpandState?: boolean; // Remembers collapse state in the search menu item in the left navigation pane
  itemPageLeftPaneSize?: number; // Last known left pane size in the item details page
  showDateFilter?: boolean; // Whether the date filter should show or not
  includeSubfolders?: boolean; // Corresponds to the "Search in sub folders" feature in the header
  drawerVisible?: boolean; // Whether or not to show the left navigation pane
  drawerWidth?: number; // Last known size of the left navigation pane
  bookmarksOpened?: string[]; // Remember opened state on bookmark subtree
  bookmarksV2Opened?: BookmarkV2OpenedState; // Remember opened state on bookmarkV2 subtrees
  bookmarkMenuOpened?: boolean; // Remember opened state on the bookmark menu item itself
  thirdColumn?: string | null; // Which third column is open in search, if any
  compactColumnSizesMap?: Record<string, Record<string, number>>;
  isFolderTreeActive?: boolean; // Remember folder collapse state
  showPlayoutDevices?: boolean; // Remember playout drawer state
  splitPaneSizes?: Record<string, number>; // Dynamic split pane sizes
  showAudioLevels?: Record<string, boolean>; // Per-context audio level meter visibility ('player', 'cutter')
  showAudioWaveform?: Record<string, boolean>; // Per-context source-preview audio waveform visibility ('sourcePreview')
  audioTrackHeight?: number; // Custom audio track height in cutter timeline
  showAudioBoxTitles?: boolean;
  audioRecordingDeviceId?: string; // Preferred microphone for cutter audio track recording; absent means the system default
  audioRecordingNoiseSuppression?: boolean; // Undefined defaults to true (the browser's own default)
  audioRecordingEchoCancellation?: boolean; // Undefined defaults to true (the browser's own default)
  audioRecordingFolderId?: string | null; // Target folder for cutter audio track recordings; undefined follows the work folder, if any
}

export interface BookmarkV2OpenedState {
  private: string[];
  system: string[];
}

export interface SchedulerPageSettings {
  location: string;
  currentPxPerMinute: number;
  timeFormat: TimeFormat;
}

export const LastSelectedFormIdSupportedItemTypesArray = [
  ItemType.VIDEO,
  ItemType.AUDIO,
  ItemType.IMAGE,
  ItemType.CLIP_LIST,
  ItemType.TIMELINE,
  ItemType.FILE,
] as const;
export type LastSelectedFormIdSupportedItemTypes =
  (typeof LastSelectedFormIdSupportedItemTypesArray)[number];
export type LastSelectedFormIds = {
  [key in LastSelectedFormIdSupportedItemTypes]: string | null; // Null indicates there is no last selected form id
};

export interface MdfSectionSettings {
  [sectionId: string]: {
    collapsed: boolean;
  };
}

export type MdfSectionsSettings = {
  [K in ViewType]?: MdfSectionSettings;
};

const isValidSettings = (data: unknown): data is MdfSectionsSettings => {
  // This is only a simple structure check, not a validation of the actual content.
  return typeof data === 'object' && data !== null;
};

const getLocalStoreMdfSectionsSettings = (): MdfSectionsSettings => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE.MDF_SECTIONS_SETTINGS);
    if (raw) {
      const parsed = JSON.parse(raw);
      return isValidSettings(parsed) ? parsed : {};
    }
  } catch (error) {
    // Failed to parse MDF sections settings, but this is not critical
  }
  return {};
};

const setLocalStoreMdfSectionsSettings = (settings: MdfSectionsSettings): void => {
  if (!isValidSettings(settings)) {
    // Invalid MDF sections settings provided, not saving to localStorage
    return;
  }
  localStorage.setItem(LOCAL_STORAGE.MDF_SECTIONS_SETTINGS, JSON.stringify(settings));
};

export function getMdfSectionDefaultCollapsed(view: ViewType, sectionId: string): boolean | null {
  const settings = getLocalStoreMdfSectionsSettings();
  return settings[view]?.[sectionId]?.collapsed ?? null;
}

export function setMdfSectionDefaultCollapsed(
  view: ViewType,
  sectionId: string,
  collapsed: boolean,
): void {
  const settings = getLocalStoreMdfSectionsSettings();

  if (!settings[view]) {
    settings[view] = {};
  }
  settings[view][sectionId] = { collapsed };
  setLocalStoreMdfSectionsSettings(settings);
}

export const getDialogChoices = (): DialogChoices => {
  const dialogChoices = localStorage.getItem(LOCAL_STORAGE.DIALOG_CHOICES);
  const parsed = parseOrNull<DialogChoices>(new ConsoleLogger(), dialogChoices);
  return {
    ...defaultDialogChoices(),
    ...parsed,
  };
};

export const setDialogChoices = (choices: DialogChoices) => {
  localStorage.setItem(LOCAL_STORAGE.DIALOG_CHOICES, JSON.stringify(choices));
};

const isUserInterfaceSettings = (settings: unknown): settings is UserInterfaceSettings => {
  if (settings && typeof settings === 'object' && settings !== null) {
    const isProbablyUserInterfaceSettings = settings as Partial<UserInterfaceSettings>;
    return (
      isProbablyUserInterfaceSettings.playerVolume !== undefined &&
      isProbablyUserInterfaceSettings.searchThumbnailSize !== undefined
    );
  }
  return false;
};

const isSchedulerPageSettings = (settings: unknown): settings is SchedulerPageSettings => {
  if (settings && typeof settings === 'object') {
    const isProbablySchedulerPageSettings = settings as SchedulerPageSettings;
    return isProbablySchedulerPageSettings.currentPxPerMinute !== undefined;
  }

  return false;
};

export const getUISettings = (): UserInterfaceSettings => {
  const settingsRaw = localStorage.getItem(LOCAL_STORAGE.GUI_SETTINGS);
  if (settingsRaw) {
    try {
      const settings: unknown = JSON.parse(settingsRaw);
      if (isUserInterfaceSettings(settings)) {
        if (settings.isMuted === undefined) {
          settings.isMuted = false;
        }
        if (isObject(settings.thirdColumn)) {
          const legacyValue = settings.thirdColumn as { key?: string };
          settings.thirdColumn = legacyValue.key ?? null;
        }
        return settings;
      } else {
        throw new Error('Unexpected ui settings object in local storage.');
      }
    } catch (error) {
      // No need to really throw an error for this minor feature for now.
    }
  }
  return { isMuted: false, playerVolume: 1, searchThumbnailSize: 160 };
};

export const getBookmarkV2OpenedState = (): BookmarkV2OpenedState => {
  const currentSettings = getUISettings();
  if (currentSettings.bookmarksV2Opened) {
    return currentSettings.bookmarksV2Opened;
  }
  return { private: [], system: [] };
};

export const setBookmarkV2OpenedState = (openedState: BookmarkV2OpenedState) => {
  const currentSettings = getUISettings();
  const newSettings = {
    ...currentSettings,
    bookmarksV2Opened: openedState,
  };
  setUISettings(newSettings);
};

export const setUISettings = (settings: UserInterfaceSettings) => {
  localStorage.setItem(LOCAL_STORAGE.GUI_SETTINGS, JSON.stringify(settings));
};

export const getSchedulerPageSettings = (): SchedulerPageSettings => {
  const settings = localStorage.getItem(LOCAL_STORAGE.SCHEDULER_PAGE_SETTINGS);
  const defaultSettings: SchedulerPageSettings = {
    location: '',
    currentPxPerMinute: 1,
    timeFormat: '24h',
  };
  if (settings) {
    const schedulerPageSettings: unknown = JSON.parse(settings);
    if (isSchedulerPageSettings(schedulerPageSettings)) {
      return {
        ...defaultSettings,
        ...schedulerPageSettings,
      };
    }
  }
  return defaultSettings;
};

export const setSchedulerPageSettings = (value: SchedulerPageSettings) => {
  localStorage.setItem(LOCAL_STORAGE.SCHEDULER_PAGE_SETTINGS, JSON.stringify(value));
};

export const getDefaultStorage = () => localStorage.getItem(LOCAL_STORAGE.DEFAULT_STORAGE);

export const setDefaultStorage = (value: string | undefined) => {
  if (!value) {
    localStorage.removeItem(LOCAL_STORAGE.DEFAULT_STORAGE);
  } else {
    localStorage.setItem(LOCAL_STORAGE.DEFAULT_STORAGE, value);
  }
};

export const getViewSize = (): ViewSize => {
  const size = localStorage.getItem(LOCAL_STORAGE.VIEW_SIZE);
  if (size) {
    return size as ViewSize;
  }
  return isPremiere() ? 'small' : 'large';
};

export const setViewSize = (size: ViewSize) => {
  localStorage.setItem(LOCAL_STORAGE.VIEW_SIZE, size);
};

export const setIsAutoRefreshSearch = (isAutoRefresh: boolean) => {
  localStorage.setItem(LOCAL_STORAGE.AUTO_REFRESH_SEARCH, isAutoRefresh.toString());
};

export const getIsAutoRefreshSearch = (): boolean => {
  const value = localStorage.getItem(LOCAL_STORAGE.AUTO_REFRESH_SEARCH);
  return value === 'true';
};

export const saveSortParams = (params: SortParameters) => {
  if (isInstanceOfSortParameters(params)) {
    localStorage.setItem(LOCAL_STORAGE.SORT_PARAMS, JSON.stringify(params));
  }
};

export const loadSortParams = (): SortParameters => {
  const params = localStorage.getItem(LOCAL_STORAGE.SORT_PARAMS);
  if (params) {
    const sortParams = JSON.parse(params);
    if (isInstanceOfSortParameters(sortParams)) {
      return sortParams;
    }
  }

  return {
    property: SortOptions.ITEM_CREATED_DATE,
    order: SortOrder.DESCENDING,
  };
};

export const getSelectedCompactViewFields = (): Record<string, string[]> => {
  const params = localStorage.getItem(LOCAL_STORAGE.SELECTED_COMPACT_VIEW_FIELDS);
  if (!params) {
    return {};
  }
  return JSON.parse(params) as Record<string, string[]>;
};

export const setSelectedCompactViewFields = (
  selectedCompactViewFields: Record<string, string[]>,
) => {
  localStorage.setItem(
    LOCAL_STORAGE.SELECTED_COMPACT_VIEW_FIELDS,
    JSON.stringify(selectedCompactViewFields),
  );
};

export const getSelectedLiveRecordingSourceProtocol = (): SupportedLiveSourceProtocol => {
  const selected = localStorage.getItem(LOCAL_STORAGE.LIVE_RECORDING_SOURCE_PROTOCOL);
  if (
    !selected ||
    !supportedLiveSourceProtocols.includes(selected as SupportedLiveSourceProtocol)
  ) {
    return 'srt-listener';
  }
  return selected as SupportedLiveSourceProtocol;
};

export const setSelectedLiveRecordingSourceProtocol = (value: SupportedLiveSourceProtocol) => {
  localStorage.setItem(LOCAL_STORAGE.LIVE_RECORDING_SOURCE_PROTOCOL, value);
};

/**
 * @deprecated Use getPreferredCentralConformKeldaId() instead.
 *
 * Returns the Kelda configuration ID that the user last selected as the target
 * conform location in the last sequence export operation.
 */
export const getLocalStoragePreferredCentralConformKeldaId = (): string | null => {
  return localStorage.getItem(LOCAL_STORAGE.CENTRAL_CONFORM_KELDA_ID);
};

/**
 * @deprecated Use setPreferredCentralConformKeldaId() instead.
 *
 * Update the Kelda configuration ID that the user has selected when exporting
 * sequence centrally.
 */
export const setLocalStoragePreferredCentralConformKeldaId = (value: string) => {
  localStorage.setItem(LOCAL_STORAGE.CENTRAL_CONFORM_KELDA_ID, value);
};

const LOCAL_STORAGE_LAST_SELECTED_KEYS = {
  [ItemType.VIDEO]: LOCAL_STORAGE.LAST_SELECTED_VIDEO_FORM_ID,
  [ItemType.IMAGE]: LOCAL_STORAGE.LAST_SELECTED_IMAGE_FORM_ID,
  [ItemType.AUDIO]: LOCAL_STORAGE.LAST_SELECTED_AUDIO_FORM_ID,
  [ItemType.FILE]: LOCAL_STORAGE.LAST_SELECTED_FILE_FORM_ID,
  [ItemType.CLIP_LIST]: LOCAL_STORAGE.LAST_SELECTED_CLIP_LIST_FORM_ID,
  [ItemType.TIMELINE]: LOCAL_STORAGE.LAST_SELECTED_TIMELINE_FORM_ID,
};

export const getLastSelectedFormIds = (): LastSelectedFormIds => {
  const lastSelectedFormIds: LastSelectedFormIds = {
    [ItemType.VIDEO]: null,
    [ItemType.IMAGE]: null,
    [ItemType.AUDIO]: null,
    [ItemType.FILE]: null,
    [ItemType.CLIP_LIST]: null,
    [ItemType.TIMELINE]: null,
  };

  Object.keys(LOCAL_STORAGE_LAST_SELECTED_KEYS).forEach((itemType) => {
    if (!isValidItemTypeForLastSelectedFormIds(itemType)) {
      return;
    }
    const key = LOCAL_STORAGE_LAST_SELECTED_KEYS[itemType];
    const id = localStorage.getItem(key);
    if (id) {
      lastSelectedFormIds[itemType] = id;
    }
  });

  return lastSelectedFormIds;
};

export const setLastSelectedFormId = (id: string, type: LastSelectedFormIdSupportedItemTypes) => {
  const key = LOCAL_STORAGE_LAST_SELECTED_KEYS[type];
  localStorage.setItem(key, id);
};

export function isValidItemTypeForLastSelectedFormIds(
  type: any,
): type is LastSelectedFormIdSupportedItemTypes {
  return LastSelectedFormIdSupportedItemTypesArray.includes(type);
}

export function getDebugItemId(): string | null {
  return localStorage.getItem('MIMIR_DEBUG_ITEM_ID');
}

export const getLastFetchedStageResponse = ():
  | GlobalApiResponse<GlobalUserResponseDto>
  | undefined => {
  const value = localStorage.getItem(LOCAL_STORAGE.LAST_FETCHED_STAGE_RESPONSE);
  const parsedValue = value ? JSON.parse(value) : null;
  if (parsedValue && parsedValue['data'] && parsedValue['data']['stages']) {
    return parsedValue as GlobalApiResponse<GlobalUserResponseDto>;
  } else {
    return undefined;
  }
};

export const setLastFetchedStageResponse = (
  stageResponse: GlobalApiResponse<GlobalUserResponseDto> | undefined,
) => {
  const stringifiedValue = stageResponse ? JSON.stringify(stageResponse) : JSON.stringify({});
  localStorage.setItem(LOCAL_STORAGE.LAST_FETCHED_STAGE_RESPONSE, stringifiedValue);
};

export const setRememberMeGlobalLogin = (remember: boolean) => {
  if (remember) {
    localStorage.setItem(LOCAL_STORAGE.REMEMBER_ME_GLOBAL_LOGIN, remember.toString());
  } else {
    localStorage.removeItem(LOCAL_STORAGE.REMEMBER_ME_GLOBAL_LOGIN);
  }
};

export const getRememberMeGlobalLogin = (): boolean => {
  const value = localStorage.getItem(LOCAL_STORAGE.REMEMBER_ME_GLOBAL_LOGIN);
  return value === 'true';
};

export function mimirDevEnabled(): boolean {
  const value = localStorage.getItem('MIMIR_DEV');
  if (value === null) {
    return false;
  }
  return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
}

export function cutterForceSystemMemoryBuffers(): boolean {
  const value = localStorage.getItem('CUTTER_FORCE_SYSTEM_MEMORY_BUFFERS');
  if (value === null) {
    return false;
  }
  return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
}

export function cutterForceSoftwareDecoder(): boolean {
  const value = localStorage.getItem('CUTTER_FORCE_SOFTWARE_DECODER');
  if (value === null) {
    return false;
  }
  return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
}

export const getTimelineDebuggerEnabled = (): boolean => {
  const value = localStorage.getItem(LOCAL_STORAGE.TIMELINE_DEBUGGER_ENABLED);
  return value === 'true';
};

export const setTimelineDebuggerEnabled = (enabled: boolean): void => {
  localStorage.setItem(LOCAL_STORAGE.TIMELINE_DEBUGGER_ENABLED, enabled.toString());
};

/** Developer-only, set from the devtools console; read once at panel start. */
export const getPanelDebugLoggingEnabled = (): boolean => {
  const value = localStorage.getItem(LOCAL_STORAGE.PANEL_DEBUG_LOGGING_ENABLED);
  return value === 'true';
};

export function getCachedResolvedTheme(): ResolvedTheme | null {
  const value = localStorage.getItem(LOCAL_STORAGE.CACHED_RESOLVED_THEME);
  return value === 'light' || value === 'dark' ? value : null;
}

export function setCachedResolvedTheme(theme: ResolvedTheme): void {
  localStorage.setItem(LOCAL_STORAGE.CACHED_RESOLVED_THEME, theme);
}

export function getLastUsedCutterPreviewPresetId(): string | null {
  return localStorage.getItem(LOCAL_STORAGE.LAST_USED_CUTTER_PREVIEW_PRESET_ID);
}

export function setLastUsedCutterPreviewPresetId(presetId: string): void {
  return localStorage.setItem(LOCAL_STORAGE.LAST_USED_CUTTER_PREVIEW_PRESET_ID, presetId);
}

export function getLastUsedVideoVisualizationMode(): VisualizationMode | null {
  const mode = localStorage.getItem(LOCAL_STORAGE.LAST_USED_VIDEO_VISUALIZATION_MODE);
  if (mode === 'waveform' || mode === 'spectrum') {
    return mode;
  }
  return null;
}

export function setLastUsedVideoVisualizationMode(mode: VisualizationMode | null): void {
  if (mode) {
    localStorage.setItem(LOCAL_STORAGE.LAST_USED_VIDEO_VISUALIZATION_MODE, mode);
  } else {
    localStorage.removeItem(LOCAL_STORAGE.LAST_USED_VIDEO_VISUALIZATION_MODE);
  }
}

export function setLastUsedVideoPlayerQuality(quality: PlaybackQuality | null): void {
  if (quality) {
    localStorage.setItem(LOCAL_STORAGE.LAST_USED_VIDEO_PLAYER_QUALITY, quality);
  } else {
    localStorage.removeItem(LOCAL_STORAGE.LAST_USED_VIDEO_PLAYER_QUALITY);
  }
}

export function getLastUsedVideoPlayerQuality(): PlaybackQuality | null {
  const quality = localStorage.getItem(LOCAL_STORAGE.LAST_USED_VIDEO_PLAYER_QUALITY);
  return quality as PlaybackQuality || null;
}

export function getLastLocalFileDisplayMode(): string | null {
  return localStorage.getItem(LOCAL_STORAGE.LAST_LOCAL_FILE_DISPLAY_MODE);
}

export function setLastLocalFileDisplayMode(filePath: string): void {
  return localStorage.setItem(LOCAL_STORAGE.LAST_LOCAL_FILE_DISPLAY_MODE, filePath);
}

export function getLastLocalFileExpanded(): boolean {
  const value = localStorage.getItem(LOCAL_STORAGE.LAST_LOCAL_FILE_EXPANDED);
  if (value === null) {
    return false;
  }
  return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
}

export function setLastLocalFileExpanded(expanded: boolean): void {
  return localStorage.setItem(LOCAL_STORAGE.LAST_LOCAL_FILE_EXPANDED, expanded ? 'true' : 'false');
}

export function getLastLocalFileFilter(): string | null {
  return localStorage.getItem(LOCAL_STORAGE.LAST_LOCAL_FILE_FILTER);
}

export function setLastLocalFileFilter(filter: string): void {
  return localStorage.setItem(LOCAL_STORAGE.LAST_LOCAL_FILE_FILTER, filter);
}
